import { type FC, useState } from "react";

import { filterByCategory } from "@/shared/constants/estateCategories";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { AddItemLayout } from "@/pages/postloss/modules/components/AddItemLayout";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { useFormWithErrors } from "@/shared/hooks/useFormWithErrors";
import { uploadDocumentWithAuth } from "@/shared/utils/uploadSupportingDocument";
import { sentryUtils } from "@/shared/utils/sentry";

import {
  LifeInsuranceForm,
  EMPTY_LIFE_INSURANCE_FORM,
  buildMetadata,
  validateLifeInsuranceFields,
  type LifeInsuranceFormValues,
  type LifeInsuranceFieldErrors,
} from "./LifeInsuranceForm";

export const AddLifeInsurance: FC<FlowCustomComponentProps> = ({ goToScreen, answers }) => {
  const { person, user } = useAppContext();
  const estateId = person?.estate_id;
  const { assets, addItem, isAddingItem } = useEstateItems(estateId);
  const hasExistingPolicies = filterByCategory(assets, "Life insurance").length > 0;

  const { form, fieldErrors, setFieldErrors, updateForm } = useFormWithErrors<
    LifeInsuranceFormValues,
    LifeInsuranceFieldErrors
  >(EMPTY_LIFE_INSURANCE_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (isSubmitting || !estateId) return;

    const errors = validateLifeInsuranceFields(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      let sourceDocumentId: number | undefined;

      if (form.documentFile) {
        sourceDocumentId = await uploadDocumentWithAuth(
          form.documentFile,
          estateId,
          "life_insurance_policy",
        );
      }

      await addItem({
        estate_id: estateId,
        category: "Life insurance",
        classification: "asset",
        item_name: form.companyName.trim(),
        item_type: form.policyType || undefined,
        balance: parseFloat(form.coverageAmount) || 0,
        metadata: buildMetadata(form),
        source_document_id: sourceDocumentId,
      });

      const updatedAnswers = {
        ...answers,
        life_insurance_review: "found",
        add_life_insurance: "",
        life_insurance_manage: "yes",
      };
      goToScreen("life_insurance_manage", updatedAnswers);
    } catch (err) {
      setError("Failed to add policy. Please try again.");
      sentryUtils.captureError(err, "AddLifeInsurance submission failed", {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <h2 className="text-center">
        {hasExistingPolicies ? "Add another life insurance policy" : "Add a life insurance policy"}
      </h2>
      <p className="mt-2 text-center text-secondary">
        Share what you know. We'll help you gather details later.
      </p>
      <div className="mt-8 w-full">
        <AddItemLayout
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          error={error}
          disabled={isAddingItem}
        >
          <LifeInsuranceForm form={form} onChange={updateForm} fieldErrors={fieldErrors} />
        </AddItemLayout>
      </div>
    </div>
  );
};
